import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { vehiclesAPI, sharingAPI } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Loader2,
  Wrench,
  Package,
  AlertTriangle,
  Clock,
  CheckCircle,
  Gauge,
  Settings,
  Plus,
  ChevronRight,
  Calendar,
  Save,
  RotateCcw,
  ClipboardList,
  BarChart3,
  Image,
  Share2,
  Copy,
  ExternalLink,
  Mountain,
} from "lucide-react";

const StatusBadge = ({ status }) => {
  if (status === "Overdue") {
    return (
      <span className="badge-overdue flex items-center gap-1">
        <span className="status-indicator status-overdue" />
        Overdue
      </span>
    );
  }
  if (status === "DueSoon") {
    return (
      <span className="badge-due-soon flex items-center gap-1">
        <span className="status-indicator status-due-soon" />
        Due Soon
      </span>
    );
  }
  return (
    <span className="badge-ok flex items-center gap-1">
      <span className="status-indicator status-ok" />
      OK
    </span>
  );
};

export default function VehicleDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [mileageInput, setMileageInput] = useState("");
  const [updatingMileage, setUpdatingMileage] = useState(false);
  const [showMileageEdit, setShowMileageEdit] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState({ is_public: false, share_url: null });
  const [togglingShare, setTogglingShare] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadShareStatus();
  }, [id]);

  const loadDashboard = async () => {
    try {
      const { data } = await vehiclesAPI.dashboard(id);
      setDashboard(data);
      setMileageInput(data.vehicle.mileage_current || "");
    } catch (error) {
      toast.error("Failed to load vehicle dashboard");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const loadShareStatus = async () => {
    try {
      const { data } = await sharingAPI.status(id);
      setShareStatus(data);
    } catch (error) {
      console.error("Failed to load share status:", error);
    }
  };

  const toggleSharing = async () => {
    setTogglingShare(true);
    try {
      const { data } = await sharingAPI.toggle(id);
      setShareStatus(data);
      if (data.is_public) {
        toast.success("Build sheet is now public!");
      } else {
        toast.success("Build sheet is now private");
      }
    } catch (error) {
      toast.error("Failed to update sharing");
    } finally {
      setTogglingShare(false);
    }
  };

  const copyShareLink = () => {
    const fullUrl = window.location.origin + shareStatus.share_url;
    navigator.clipboard.writeText(fullUrl);
    toast.success("Link copied to clipboard!");
  };

  const handleSocialShare = (platform) => {
    const fullUrl = window.location.origin + shareStatus.share_url;
    const title = `Check out ${dashboard?.vehicle?.nickname}'s build sheet!`;
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(fullUrl)}&text=${encodeURIComponent(title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`,
      reddit: `https://www.reddit.com/submit?url=${encodeURIComponent(fullUrl)}&title=${encodeURIComponent(title)}`,
    };
    window.open(urls[platform], "_blank", "width=600,height=400");
  };

  const handleMileageUpdate = async () => {
    const newMileage = parseInt(mileageInput);
    if (isNaN(newMileage) || newMileage < 0) {
      toast.error("Please enter a valid mileage");
      return;
    }

    setUpdatingMileage(true);
    try {
      await vehiclesAPI.update(id, { mileage_current: newMileage });
      toast.success("Mileage updated");
      setShowMileageEdit(false);
      // Reload dashboard to refresh service statuses
      await loadDashboard();
    } catch (error) {
      toast.error("Failed to update mileage");
    } finally {
      setUpdatingMileage(false);
    }
  };

  const cancelMileageEdit = () => {
    setMileageInput(dashboard?.vehicle?.mileage_current || "");
    setShowMileageEdit(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!dashboard) return null;

  const { vehicle, overdue_services, due_soon_services, recent_services, total_services, total_mods } = dashboard;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="back-to-garage-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />
                <div>
                  <h1 className="text-lg font-bold text-foreground">
                    {vehicle.nickname}
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ")}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShareModalOpen(true)}
                data-testid="share-btn"
                className="text-muted-foreground hover:text-foreground"
              >
                <Share2 className="w-5 h-5" />
              </Button>
              <Link
                to={`/vehicles/${id}/edit`}
                data-testid="edit-vehicle-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Settings className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions & Stats */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {/* Mileage Card - With Quick Update */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              {showMileageEdit ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-primary" />
                    <span className="text-xs text-muted-foreground uppercase">Update Mileage</span>
                  </div>
                  <Input
                    type="number"
                    value={mileageInput}
                    onChange={(e) => setMileageInput(e.target.value)}
                    data-testid="mileage-update-input"
                    placeholder="Enter mileage"
                    className="bg-zinc-900 border-zinc-700 font-mono h-9"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleMileageUpdate}
                      disabled={updatingMileage}
                      data-testid="save-mileage-btn"
                      className="flex-1 h-8 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      {updatingMileage ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Save className="w-3 h-3" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelMileageEdit}
                      data-testid="cancel-mileage-btn"
                      className="flex-1 h-8 border-zinc-700"
                    >
                      <RotateCcw className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  className="flex items-center gap-3 cursor-pointer group"
                  onClick={() => setShowMileageEdit(true)}
                  data-testid="mileage-card"
                >
                  <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Gauge className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Current Mileage</p>
                    <p className="text-xl font-mono font-bold text-foreground">
                      {vehicle.mileage_current?.toLocaleString() || "---"}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                    Click to update
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Services Count */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Services</p>
                  <p className="text-xl font-mono font-bold text-foreground">{total_services}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mods Count */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/10 rounded-sm flex items-center justify-center">
                  <Package className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Mods</p>
                  <p className="text-xl font-mono font-bold text-foreground">{total_mods}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Health Status */}
          <Card className={`card-tactical border ${
            overdue_services.length > 0 
              ? "bg-red-500/5 border-red-500/30" 
              : due_soon_services.length > 0 
                ? "bg-amber-500/5 border-amber-500/30" 
                : "bg-green-500/5 border-green-500/30"
          }`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                  overdue_services.length > 0 
                    ? "bg-red-500/10" 
                    : due_soon_services.length > 0 
                      ? "bg-amber-500/10" 
                      : "bg-green-500/10"
                }`}>
                  {overdue_services.length > 0 ? (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  ) : due_soon_services.length > 0 ? (
                    <Clock className="w-5 h-5 text-amber-500" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Health Status</p>
                  <p className={`text-lg font-bold ${
                    overdue_services.length > 0 
                      ? "text-red-500" 
                      : due_soon_services.length > 0 
                        ? "text-amber-500" 
                        : "text-green-500"
                  }`}>
                    {overdue_services.length > 0 
                      ? `${overdue_services.length} Overdue` 
                      : due_soon_services.length > 0 
                        ? `${due_soon_services.length} Due Soon` 
                        : "All Good"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Button
            onClick={() => navigate(`/vehicles/${id}/services/new`)}
            data-testid="log-service-btn"
            className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Log Service
          </Button>
          <Button
            onClick={() => navigate(`/vehicles/${id}/mods/new`)}
            data-testid="add-mod-btn"
            variant="outline"
            className="btn-tactical border-zinc-700 hover:bg-zinc-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Mod
          </Button>
          <Button
            onClick={() => navigate(`/vehicles/${id}/maintenance`)}
            data-testid="view-maintenance-btn"
            variant="outline"
            className="btn-tactical border-zinc-700 hover:bg-zinc-800"
          >
            View All Maintenance
          </Button>
          <Button
            onClick={() => navigate(`/vehicles/${id}/build`)}
            data-testid="view-build-sheet-btn"
            variant="outline"
            className="btn-tactical border-zinc-700 hover:bg-zinc-800"
          >
            View Build Sheet
          </Button>
          <Button
            onClick={() => navigate(`/vehicles/${id}/gallery`)}
            data-testid="view-gallery-btn"
            variant="outline"
            className="btn-tactical border-zinc-700 hover:bg-zinc-800"
          >
            <Image className="w-4 h-4 mr-2" />
            Photo Gallery
          </Button>
          <Button
            onClick={() => navigate(`/vehicles/${id}/templates`)}
            data-testid="apply-template-btn"
            variant="outline"
            className="btn-tactical border-zinc-700 hover:bg-zinc-800"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Apply Template
          </Button>
          <Button
            onClick={() => navigate(`/analytics?vehicle=${id}`)}
            data-testid="view-analytics-btn"
            variant="outline"
            className="btn-tactical border-zinc-700 hover:bg-zinc-800"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Cost Analytics
          </Button>
          <Button
            onClick={() => navigate(`/vehicles/${id}/trail-logs`)}
            data-testid="trail-logs-btn"
            variant="outline"
            className="btn-tactical border-zinc-700 hover:bg-zinc-800"
          >
            <Mountain className="w-4 h-4 mr-2" />
            Trail Logs
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Overdue Services */}
          {overdue_services.length > 0 && (
            <Card className="card-tactical bg-red-500/5 border-red-500/30 lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-red-500">
                  <AlertTriangle className="w-5 h-5" />
                  Overdue Services
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overdue_services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/vehicles/${id}/services/${service.id}/edit`}
                      data-testid={`overdue-service-${service.id}`}
                      className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm border border-zinc-800 hover:border-red-500/50 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <StatusBadge status={service.status} />
                        <div>
                          <p className="font-medium text-foreground">{service.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {service.category} • Last: {new Date(service.service_date + "T00:00:00").toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {service.next_due_mileage && (
                          <span className="text-xs font-mono text-red-400">
                            Due @ {service.next_due_mileage.toLocaleString()} mi
                          </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-red-500" />
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Due Soon Services */}
          {due_soon_services.length > 0 && (
            <Card className="card-tactical bg-amber-500/5 border-amber-500/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-500">
                  <Clock className="w-5 h-5" />
                  Due Soon
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {due_soon_services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/vehicles/${id}/services/${service.id}/edit`}
                      data-testid={`due-soon-service-${service.id}`}
                      className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm border border-zinc-800 hover:border-amber-500/50 transition-colors group"
                    >
                      <div>
                        <p className="font-medium text-foreground">{service.title}</p>
                        <p className="text-xs text-muted-foreground">{service.category}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {service.next_due_mileage && (
                          <span className="text-xs font-mono text-amber-400">
                            @ {service.next_due_mileage.toLocaleString()} mi
                          </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-500" />
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Services */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  Recent Services
                </CardTitle>
                <Link
                  to={`/vehicles/${id}/maintenance`}
                  className="text-xs text-primary hover:underline"
                >
                  View All
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {recent_services.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No service records yet
                </p>
              ) : (
                <div className="space-y-2">
                  {recent_services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/vehicles/${id}/services/${service.id}/edit`}
                      data-testid={`recent-service-${service.id}`}
                      className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-sm border border-zinc-800 hover:border-zinc-700 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <StatusBadge status={service.status} />
                        <div>
                          <p className="font-medium text-foreground">{service.title}</p>
                          <p className="text-xs text-muted-foreground">{service.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-mono">
                          {new Date(service.service_date + "T00:00:00").toLocaleDateString()}
                        </span>
                        <ChevronRight className="w-4 h-4 group-hover:text-foreground" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Share Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Build Sheet
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-zinc-900 rounded-sm border border-zinc-800">
              <div>
                <p className="font-medium text-foreground">Public Build Sheet</p>
                <p className="text-sm text-muted-foreground">
                  {shareStatus.is_public 
                    ? "Anyone with the link can view your build sheet" 
                    : "Make your build sheet visible to others"}
                </p>
              </div>
              <Button
                variant={shareStatus.is_public ? "default" : "outline"}
                onClick={toggleSharing}
                disabled={togglingShare}
                className={shareStatus.is_public ? "bg-primary" : "border-zinc-700"}
              >
                {togglingShare ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : shareStatus.is_public ? (
                  "Public"
                ) : (
                  "Make Public"
                )}
              </Button>
            </div>

            {shareStatus.is_public && shareStatus.share_url && (
              <>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Share Link</p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={window.location.origin + shareStatus.share_url}
                      readOnly
                      className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyShareLink}
                      className="border-zinc-700 flex-shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <a
                      href={shareStatus.share_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0"
                    >
                      <Button
                        variant="outline"
                        size="icon"
                        className="border-zinc-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                </div>

                {/* QR Code */}
                <div className="flex flex-col items-center p-4 bg-white rounded-lg">
                  <QRCodeSVG
                    value={window.location.origin + shareStatus.share_url}
                    size={160}
                    level="M"
                    data-testid="share-qr-code"
                  />
                  <p className="text-xs text-zinc-500 mt-2">Scan to view build sheet</p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Share on Social</p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleSocialShare("twitter")}
                      className="border-zinc-700 flex-1"
                    >
                      𝕏 Twitter
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSocialShare("facebook")}
                      className="border-zinc-700 flex-1"
                    >
                      f Facebook
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSocialShare("reddit")}
                      className="border-zinc-700 flex-1"
                    >
                      r/ Reddit
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
